import { motion } from "framer-motion";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, Flame, Link2, Download } from "lucide-react";
import { API } from "@/App";
import { downloadShareCard } from "@/lib/shareCardImage";

export default function ShareCardSection({ shareCard, shareLoading, onGenerate }) {
  // /api/share/page serves OG meta tags for social previews, then redirects to /s/{id}
  const publicUrl = shareCard ? `${API}/share/page/${shareCard.share_id}` : null;
  const isWinStreak = shareCard?.streak_type === "win" && shareCard?.current_streak > 0;

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Public link copied!");
  };

  const nativeShare = async () => {
    const text = isWinStreak
      ? `I'm on a ${shareCard.current_streak}-trade win streak on TradeInsights AI 🔥`
      : `Check out my trading performance on TradeInsights AI`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "TradeInsights AI", text, url: publicUrl });
      } catch (err) {
        console.debug("Native share dismissed:", err);
      }
    } else {
      copyLink();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45 }}
      className="mt-6"
    >
      <Card className="glass-effect border border-[#F59E0B]/10">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-[#F59E0B]" />
              <h3 className="text-sm font-bold text-white">Share Your Streak</h3>
            </div>
            <Button
              data-testid="generate-share-card-btn"
              size="sm"
              onClick={onGenerate}
              disabled={shareLoading}
              className="bg-[#F59E0B] text-black hover:bg-[#F59E0B]/90 text-xs font-bold"
            >
              <Share2 className="w-3.5 h-3.5 mr-1" />
              {shareLoading ? "Generating..." : "Generate Card"}
            </Button>
          </div>

          {shareCard && (
            <div data-testid="share-card-preview" className="glass-effect p-4 rounded-lg border border-[#F59E0B]/20">
              <div className="text-center mb-3">
                <div className="text-xs text-gray-400 uppercase tracking-wider">Trading Performance</div>
                <div className="text-lg font-bold text-[#F59E0B]" style={{ fontFamily: 'Unbounded' }}>
                  {shareCard.trading_style}
                </div>
                {isWinStreak && (
                  <div className="flex items-center justify-center gap-1.5 mt-1" data-testid="share-card-streak">
                    <Flame className="w-4 h-4 text-[#00FFA3]" />
                    <span className="text-sm font-bold text-[#00FFA3] mono">
                      {shareCard.current_streak} WIN STREAK
                    </span>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-3 text-center mb-3">
                <div>
                  <div className={`text-xl font-bold mono ${shareCard.win_rate >= 50 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                    {shareCard.win_rate}%
                  </div>
                  <div className="text-[10px] text-gray-500">Win Rate</div>
                </div>
                <div>
                  <div className={`text-xl font-bold mono ${shareCard.total_pnl >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                    ${shareCard.total_pnl}
                  </div>
                  <div className="text-[10px] text-gray-500">Total P&L</div>
                </div>
                <div>
                  <div className="text-xl font-bold mono text-white">{shareCard.grade}</div>
                  <div className="text-[10px] text-gray-500">Grade</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
                <span>{shareCard.wins}W / {shareCard.losses}L ({shareCard.total_trades} trades)</span>
                <span>Avg: {shareCard.avg_return >= 0 ? '+' : ''}{shareCard.avg_return}%</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  data-testid="share-native-btn"
                  size="sm"
                  onClick={nativeShare}
                  className="flex-1 bg-[#00E0FF] text-black hover:bg-[#33E9FF] text-xs font-bold"
                >
                  <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
                </Button>
                <Button
                  data-testid="share-copy-link-btn"
                  size="sm"
                  variant="outline"
                  onClick={copyLink}
                  className="flex-1 border-white/10 text-gray-300 hover:bg-white/5 hover:text-white text-xs"
                >
                  <Link2 className="w-3.5 h-3.5 mr-1.5" /> Copy Link
                </Button>
                <Button
                  data-testid="share-download-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => downloadShareCard(shareCard)}
                  className="flex-1 border-white/10 text-gray-300 hover:bg-white/5 hover:text-white text-xs"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
